import React, { useEffect, useState, Fragment } from 'react';
import {
  Box,
  FormInput,
  FormInputColumn,
  Cluster,
  constants,
  LineItem,
  BoxWithShadow,
  CollapsibleSection,
  Motion,
  Title,
  SolidDivider,
  Detail,
  ButtonWithAction,
  Checkbox,
  Text,
  util,
  ButtonWithLink,
} from '@thecb/components';
import { required } from 'redux-freeform';
import Tooltip from '../../components/tooltip';

import {
  reducer,
  mapStateToProps,
  mapDispatchToProps,
} from './CashDropForm.state';
import { CASH_ACTION_TYPES } from '../../utils/constants';
import { formatPrice } from '../../utils/helpers';
import { noop } from 'rxjs';
import { useDispatch, useSelector } from 'react-redux';
import { DynamicDenominationsFieldGroup } from '../../components/form-blocks';
import { openCashDrawer } from '../../state/peripheralDevices';
import { useNavigate } from 'react-router-dom';

const requireErrors = (error_text) => ({
  [required.error]: error_text,
});

export const CashDropForm = ({
  actions,
  fields,
  forms,
  submitCashDropForm,
  terminalSession = {},
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCashSectionOpen, setCashSectionIsOpen] = useState(false);
  const { uuid } = terminalSession;
  const [denominationsFormState, setDenominationsFormState] = useState({});
  const { denominations } = useSelector((state) => state.shared);
  const { isLoading } = useSelector((state) => state.cashDrop);

  useEffect(() => {
    return () => actions.form.clear();
  }, []);

  const handleInputClick = (event) => {
    event.stopPropagation();
  };

  const denominationsSum = isCashSectionOpen
    ? Object.entries(denominationsFormState).reduce((acc, cur) => {
        const [key, quantity] = cur;
        return (acc +=
          quantity * denominations.find((d) => d.name === key).value || 0);
      }, 0)
    : parseFloat(fields.cashTotal.rawValue || 0);

  const confirmAmountsBoolean = Boolean(fields.confirmAmounts.rawValue);
  const handleConfirmAmountsCheckboxClick = () => {
    actions.fields.confirmAmounts.set(!confirmAmountsBoolean);
  };

  const handleSubmit = () => {
    const hasDenominations = denominationsFormState && !!denominations.length;
    const reducedDenominationFields = Object.entries(
      denominationsFormState,
    ).reduce((acc, [key, quantity]) => {
      const denomination_id = denominations.find((d) => d.name === key).id;
      return quantity > 0 ? [...acc, { denomination_id, quantity }] : acc;
    }, []);
    const denominationsWithValues = hasDenominations
      ? reducedDenominationFields
      : [];
    return submitCashDropForm({
      cash_action_type: CASH_ACTION_TYPES.DROP,
      cash_actions_denominations_attributes: denominationsWithValues,
      notes: fields.notes.rawValue,
      total: denominationsSum,
      navigate,
    });
  };

  const ShowTooltip = ({ show, text, children }) => (
    <Fragment>
      {show ? (
        <Tooltip
          width="275px"
          height="95%"
          variant="error"
          left
          noIcon
          text={text}
        >
          {children}
        </Tooltip>
      ) : (
        <Fragment>{children}</Fragment>
      )}
    </Fragment>
  );

  const disabledBtnContainerStyle = `
    cursor: not-allowed;
    margin-left: -8px;
    z-index: 1;
  `;
  const negativeCashDrop = denominationsSum < 0;
  const unconfirmedAmount = !confirmAmountsBoolean;
  const showToolTip = unconfirmedAmount || negativeCashDrop;
  const toolTipText = unconfirmedAmount
    ? 'You must check Confirm Amounts above to continue'
    : 'Cash Total cannot be a negative amount';
  const handleCashDropSubmitClick = util.general.generateClickHandler(
    forms.cashDropForm,
    () => handleErrors(true),
    handleSubmit,
  );

  const cashDenominationsDropdownHeading = (
    <Box height="10rem" padding="0 0 0 1.5rem">
      <Cluster justify="space-between" align="center">
        <Box padding="0">
          <Cluster
            nowrap="true"
            overflow="visible"
            childGap="1rem"
            justify="flex-start"
          >
            <Detail margin="" as="h4">
              Cash
            </Detail>
            <Detail margin="" as="span">
              |
            </Detail>
            <ButtonWithAction
              textExtraStyles="font-size: 1rem;"
              variant="smallGhost"
              action={noop}
              text={
                isCashSectionOpen ? 'Hide Denominations' : 'Show Denominations'
              }
            />
          </Cluster>
        </Box>
        <Box padding="0">
          {isCashSectionOpen ? (
            <Cluster align="center" minHeight="5rem">
              <ButtonWithAction
                textExtraStyles="font-size: 1rem;"
                variant="smallGhost"
                action={(e) => {
                  e.stopPropagation();
                  setDenominationsFormState({});
                  actions.form.clear();
                }}
                text="Clear count"
              />
              <Detail>{formatPrice(denominationsSum)}</Detail>
            </Cluster>
          ) : (
            <Cluster overflow="visible">
              <FormInput
                data-testid="cash-total-input"
                extraStyles={`text-align: right; width: 9.5rem; margin-right: .5rem;`}
                onClick={handleInputClick}
                labelTextWhenNoError=""
                errorMessages={requireErrors('Cash Total Error')}
                field={fields.cashTotal}
                errors={fields.errors}
                fieldActions={actions.fields.cashTotal}
                labelTextVariant="p"
                errorTextVariant="p"
              />
            </Cluster>
          )}
        </Box>
      </Cluster>
    </Box>
  );

  return (
    <Box padding="0">
      <Title as="h3" margin="0.25rem 0">
        Details
      </Title>
      <BoxWithShadow
        variant="baseStandard"
        borderRadius=".25rem"
        padding="1.5rem"
        background={constants.colors.WHITE}
      >
        <Cluster justify="space-between" align="center">
          <Detail as="h5" margin=".25rem 0">
            Batch number
          </Detail>
          <Detail weight={constants.fontWeights.FONT_WEIGHT_SEMIBOLD}>
            {uuid}
          </Detail>
        </Cluster>
        <FormInput
          data-testid="notes"
          labelTextWhenNoError="Notes (optional)"
          field={fields.notes}
          errors={fields.errors}
          fieldActions={actions.fields.notes}
          labelTextVariant="p"
          errorTextVariant="p"
        />
      </BoxWithShadow>
      <Title as="h3" margin="1.5rem 0 .25rem">
        Cash Totals
      </Title>
      <BoxWithShadow
        variant="baseStandard"
        borderRadius=".25rem"
        padding="0"
        background={constants.colors.WHITE}
      >
        <Box padding="0">
          <CollapsibleSection
            supportsTouch={false}
            isOpen={isCashSectionOpen}
            title={cashDenominationsDropdownHeading}
            initiallyOpen={false}
            toggleSection={() => setCashSectionIsOpen(!isCashSectionOpen)}
            customTitle={true}
          >
            <Motion
              variants={{
                open: { opacity: 1 },
                closed: { opacity: 0 },
              }}
              initial={'open'}
            >
              <SolidDivider />
              <Box background={constants.colors.ATHENS_GREY}>
                <FormInputColumn>
                  <LineItem description="Denomination" amount="Amount" />
                  <DynamicDenominationsFieldGroup
                    denominationsFormState={denominationsFormState}
                    setDenominationsFormState={setDenominationsFormState}
                  />
                </FormInputColumn>
              </Box>
            </Motion>
          </CollapsibleSection>
        </Box>
        <SolidDivider />
        <Box padding="1.5rem">
          <Cluster justify="space-between">
            <Detail
              variant="large"
              weight={constants.fontWeights.FONT_WEIGHT_BOLD}
            >
              Total
            </Detail>
            <Detail variant="large" data-testid="total">
              {formatPrice(denominationsSum)}
            </Detail>
          </Cluster>
        </Box>
      </BoxWithShadow>
      <Box padding="2rem 0">
        <BoxWithShadow
          variant="baseStandard"
          borderRadius=".25rem"
          background={constants.colors.WHITE}
        >
          <Checkbox
            id={'confirm-amounts'}
            title={
              <Text weight={constants.fontWeights.FONT_WEIGHT_BOLD}>
                Confirm Amounts
              </Text>
            }
            name={'Confirm Amounts'}
            checked={confirmAmountsBoolean}
            onChange={handleConfirmAmountsCheckboxClick}
          />
          <Detail margin=".5rem 2.5rem 0" as="p">
            By checking the box above, I confirm the amounts above are accurate.
          </Detail>
        </BoxWithShadow>
      </Box>
      <Cluster justify="space-between" align="center">
        <ButtonWithLink
          text="Cancel"
          isLoading={false}
          variant="secondary"
          url={'/pos'}
          textExtraStyles="font-size: 1rem;"
        />
        <Cluster align="center" overflow="visible">
          <ButtonWithAction
            textExtraStyles="font-size: 1rem;"
            text="Open Drawer"
            isLoading={false}
            variant="secondary"
            action={() => dispatch(openCashDrawer({ location }))}
          />
          <ShowTooltip show={showToolTip} text={toolTipText}>
            <Box
              padding="0"
              extraStyles={showToolTip && disabledBtnContainerStyle}
            ></Box>
            <ButtonWithAction
              textExtraStyles="font-size: 1rem;"
              id="drop-cash-btn"
              disabled={isLoading || showToolTip}
              isLoading={isLoading}
              text="Drop Cash"
              data-testid="cash-drop-cta"
              action={handleCashDropSubmitClick}
            />
          </ShowTooltip>
        </Cluster>
      </Cluster>
    </Box>
  );
};

CashDropForm.reducer = reducer;
CashDropForm.mapStateToProps = mapStateToProps;
CashDropForm.mapDispatchToProps = mapDispatchToProps;
